import { Component, Prop, h, Element } from '@stencil/core';
import { AspectRatio, BackgroundColor } from '../images/image';
import { ButtonTheme } from '../button/button';

export enum Layout {
  stacked = 'stacked',
  responsive = 'responsive',
}

export enum ImageContainerWidth {
  xs = 'xs',
  s = 's',
  m = 'm',
  l = 'l',
  xl = 'xl',
}

export enum ImageContainerStackedHeight {
  s = 's',
  m = 'm',
}

export enum CTAStyle {
  primary = 'primary',
  secondary = 'secondary',
  link = 'link',
}

export interface ImageProps {
  backgroundSrc: string;
  backgroundAlt: string;
  foregroundSrc: string;
  foregroundAlt: string;
  backgroundColor?: BackgroundColor;
  poiX: number;
  poiY: number;
}

export interface CtaProps {
  label: string;
  href: string;
  style: CTAStyle;
}

@Component({
  tag: 'amp-text-section-block',
  styleUrls: ['../../base.css'],
  shadow: true,
})
export class AmpTextSectionBlock {
  @Element() el: HTMLElement;
  /**
   * Sets the foreground and background of the amp-image component
   */
  @Prop() image: ImageProps | string;
  /**
   * Sets the copy of the component
   */
  @Prop() copy: string;
  /**
   * Determines if the component is responsive (horizontal on desktop) or always stacked
   */
  @Prop() layout: Layout;
  /**
   * Determines if a responsive (inline) component has a reverse layout (image on right, copy on left)
   */
  @Prop() reverseLayout: boolean;
  /**
   * The width of the image container for responsive desktop layout: 'xs', 's', 'm' (default), 'l', 'xl'
   */
  @Prop() imageContainerWidth: ImageContainerWidth;
  /**
   * The height of the image container for stacked and mobile layouts: 's' (default), 'm'
   */
  @Prop() imageContainerStackedHeight: ImageContainerStackedHeight;
  /**
   * Aspect Ratio for the image: auto (default), square, video, video-vertical
   */
  @Prop() aspectRatio: AspectRatio;
  /**
   * Array of CTAs with { label, href, style }
   */
  @Prop() ctas: CtaProps[] | string;
  /**
   * Comes from the 3rd piece of the tracking code that is selected on the content type
   */
  @Prop() dataCampaignName: string;
  /**
   * The entire tracking code that merch/marketing use for reporting purposes
   */
  @Prop() primaryTrackingId: string;
  /**
   * The entire tracking code that merch/marketing use for reporting purposes
   */
  @Prop() secondaryTrackingId: string;

  private threshold: number = 9;

  componentDidLoad() {
    const ulElement = this.el.shadowRoot.querySelector('ul');
    const liItems = ulElement?.querySelectorAll('li');

    const olElement = this.el.shadowRoot.querySelector('ol');
    const olLiItems = olElement?.querySelectorAll('li');

    if (liItems && liItems.length <= this.threshold) {
      ulElement.style.columnCount = '1';
    }

    if (olLiItems && olLiItems.length <= this.threshold) {
      olElement.style.columnCount = '1';
    }
  }

  render() {
    const image = typeof this.image === 'string' ? JSON.parse(this.image) : this.image;
    const copy = this.copy;
    const layout = this.layout || Layout.responsive;
    const reverseLayout = this.reverseLayout || false;
    const imageContainerWidth = this.imageContainerWidth || ImageContainerWidth.m;
    const imageContainerStackedHeight = this.imageContainerStackedHeight || ImageContainerStackedHeight.s;
    const aspectRatio = this.aspectRatio || AspectRatio.auto;
    const ctas = typeof this.ctas === 'string' ? JSON.parse(this.ctas) : [];
    const dataCampaignName = this.dataCampaignName;
    const primaryTrackingId = this.primaryTrackingId;
    const secondaryTrackingId = this.secondaryTrackingId;

    const imgContainerW =
      imageContainerWidth === ImageContainerWidth.m
        ? `md:col-span-4`
        : imageContainerWidth === ImageContainerWidth.xs
          ? `md:col-span-2`
          : imageContainerWidth === ImageContainerWidth.s
            ? `md:col-span-3`
            : imageContainerWidth === ImageContainerWidth.l
              ? `md:col-span-5`
              : imageContainerWidth === ImageContainerWidth.xl
                ? `md:col-span-6`
                : ``;

    const copyContainerW =
      imageContainerWidth === ImageContainerWidth.m
        ? `md:col-span-8`
        : imageContainerWidth === ImageContainerWidth.xs
          ? `md:col-span-10`
          : imageContainerWidth === ImageContainerWidth.s
            ? `md:col-span-9`
            : imageContainerWidth === ImageContainerWidth.l
              ? `md:col-span-7`
              : imageContainerWidth === ImageContainerWidth.xl
                ? `md:col-span-6`
                : ``;

    const imgContainerHeight =
      imageContainerStackedHeight === ImageContainerStackedHeight.s ? 'h-[200px]' : imageContainerStackedHeight === ImageContainerStackedHeight.m ? 'h-[250px]' : 'h-[200px]';

    const isResponsive = layout === Layout.responsive;
    const hasCTA = ctas.length > 0;

    return (
      <div class={`grid grid-cols-1 ${isResponsive && `md:grid-cols-12 md:gap-8`} gap-4 min-h-[250px]`}>
        {image && (
          <div
            class={`col-span-12 ${isResponsive && imgContainerW} ${imgContainerHeight} px-0 ${isResponsive && 'md:relative md:h-auto md:min-h-[250px] md:max-h-none'} ${reverseLayout ? 'md:order-1' : ''} ${isResponsive && `md:aspect-${aspectRatio}`}`}
          >
            <div class={`${isResponsive && aspectRatio === AspectRatio.auto ? 'md:absolute' : ''} w-full h-full`}>
              <amp-image
                background-src={image.backgroundSrc}
                background-alt={image.backgroundAlt}
                foreground-src={image.foregroundSrc}
                foreground-alt={image.foregroundAlt}
                background-color={image.backgroundColor}
                aspect-ratio={'auto'}
                poi-x={image.poiX}
                poi-y={image.poiY}
              ></amp-image>
            </div>
          </div>
        )}
        <div class={`col-span-12 ${isResponsive && image && copyContainerW} flex flex-col content-start items-start gap-4 text-left p-0`}>
          {copy && <div class="amp-rte-container flex flex-col gap-4" innerHTML={copy}></div>}
          {hasCTA && (
            <div class="flex flex-row gap-4 flex-wrap analytics-tracking">
              {ctas.map((cta, index) =>
                cta.style !== CTAStyle.link ? (
                  <a
                    key={index}
                    href={cta.href}
                    data-campaign-name={dataCampaignName ? dataCampaignName : null}
                    data-analytics-trackingid={index === 0 ? primaryTrackingId : secondaryTrackingId}
                  >
                    <amp-button type={cta.style === CTAStyle.primary ? ButtonTheme.primary : ButtonTheme.secondary}>{cta.label}</amp-button>
                  </a>
                ) : (
                  <amp-text-link
                    key={index}
                    href={cta.href}
                    icon="ArrowRight"
                    data-campaign-name={dataCampaignName ? dataCampaignName : null}
                    data-analytics-trackingid={index === 0 ? primaryTrackingId : secondaryTrackingId}
                  >
                    {cta.label}
                  </amp-text-link>
                ),
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}
